// Vérification spécifique pour numberCompany
import { BankAccountService } from '../../Services';

export const formatNumberCompany = (value: string) => {
    let val = value.replace(/\D/g, ''); // Remove all non-digits
    if (val) {
        val =
            val
                .substring(0, 10)
                .match(/^\d{1,4}|\d{1,3}/g)
                ?.join('.') || '';
    }
    return val;
};

export const checkIfKnownIbanBceCombination = async (
    iban: string,
    numberCompany: string
): Promise<boolean> => {
    if (!iban || !numberCompany) return false;

    const cleanIban = iban.replace(/\s/g, '');
    const cleanNumber = numberCompany.replace(/\./g, '');

    try {
        const response = await BankAccountService.checkBankAccount(
            cleanIban,
            cleanNumber
        );
        return response;
    } catch (error) {
        console.error(error);
        return false;
    }
};

export const formatNumberAddress = (value: string) => {
    return value.replace(/[f-zF-Z _\-*\\]/g, '');
};

export const formatPhone = (value: string): string => {
    return value.replace(/\D/g, '');
};

interface FileValidationOptions<T> {
    fieldName: keyof T;
    setData: React.Dispatch<React.SetStateAction<T>>;
    maxSize?: number; // taille max en octets
    allowedExtensions?: string[];
}

export const handleFileValidation = <T>(
    file: File | null,
    options: FileValidationOptions<T>
) => {
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();
    const maxSize = options.maxSize ?? 5_000_000;
    const allowedExtensions = options.allowedExtensions ?? [
        'doc',
        'docx',
        'xls',
        'xlsx',
        'pdf',
        'jpg',
        'jpeg',
        'png',
        'gif',
    ];

    if (
        file.size === 0 ||
        file.size > maxSize ||
        !ext ||
        !allowedExtensions.includes(ext)
    ) {
        /* empty */
    } else {
        options.setData((prev) => ({ ...prev, [options.fieldName]: file }));
    }
};

export const handleDeleteFile = <T>(
    fieldName: keyof T,
    setData: React.Dispatch<React.SetStateAction<T>>
) => {
    setData((prev) => ({ ...prev, [fieldName]: null }));
};

export const isValidNationalRegisterNumber = (nrNumber: string): boolean => {
    nrNumber = nrNumber.replace(/\D/g, '');
    const controlNumber = parseInt(nrNumber.substring(9, 11));
    const numberToControl = nrNumber.substring(0, 9);

    const controlBefore2000 = 97 - (parseInt(numberToControl) % 97) || 97;
    const controlAfter2000 = 97 - (parseInt('2' + numberToControl) % 97) || 97;

    return (
        controlNumber === controlBefore2000 ||
        controlNumber === controlAfter2000
    );
};

export function formatNationalRegister(value: string): string {
    const digits = value.replace(/\D/g, '');
    const match = digits.match(
        /(\d{1,2})(\d{1,2})?(\d{1,2})?(\d{1,3})?(\d{1,2})?/
    );

    if (!match) return value;

    const [, a, b, c, d, e] = match;

    return (
        `${a}` +
        (b ? `.${b}` : '') +
        (c ? `.${c}` : '') +
        (d ? `-${d}` : '') +
        (e ? `.${e}` : '')
    );
}

// prettier-ignore
export const isValidIBANNumber = (input: any): number | string | boolean => {
    const CODE_LENGTHS: Record<string, number> = {
        AD: 24, AE: 23, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
        CH: 21, CR: 21, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, ES: 24,
        FI: 18, FO: 18, FR: 27, GB: 22, GI: 23, GL: 18, GR: 27, GT: 28, HR: 21,
        HU: 28, IE: 22, IL: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28,
        LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27,
        MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29,
        RO: 24, RS: 22, SA: 24, SE: 24, SI: 19, SK: 24, SM: 27, TN: 24, TR: 26,
        AL: 28, BY: 28, EG: 29, GE: 22, IQ: 23, LC: 32, SC: 31, ST: 25,
        SV: 28, TL: 23, UA: 29, VA: 22, VG: 24, XK: 20
    };

    const iban = String(input)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '');
    const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);

    if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
        return false;
    }

    const digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter) =>
        (letter.charCodeAt(0) - 55).toString()
    );

    return mod97(digits);
};

const mod97 = (str: string): number => {
    let checksum: any = str.slice(0, 2);

    for (let offset = 2; offset < str.length; offset += 7) {
        const fragment = checksum + str.substring(offset, offset + 7);
        checksum = parseInt(fragment, 10) % 97;
    }

    return checksum;
};
